import { formatPence, formatPrice, shortDate } from './money';
import { autoSpendState, spendCopy } from './spendRules';
import { baileyQuota, cardState, isSelfEmployedView, isUpgraded, meter, planSummary, receptionistMeters, receptionistState, upgradePricePence, BAILEY_PLUS } from './usageRules';

/**
 * Bailey's summary at the top of Plan & billing: one or two sentences, in
 * Bailey's voice, from /billing/usage. The first says where they are; the
 * second is the one thing worth knowing this month (an issue first, else the
 * most-used meter and "nothing to worry about").
 *
 *   { lines: [string, string?], tone: 'ok'|'warn'|'bad', issue, action }
 *   issue:  'settlement-failed' | 'trial-ending' | 'negative-balance' |
 *           'receptionist-100' | 'receptionist-80' | 'bailey-out' | null
 *   action: the one button that fixes it, or null (then the hero has no
 *           button and the plan card holds the upgrade)
 *           ('card' | 'add-card' | 'resume' | 'top-up' | 'receptionist')
 * Order: failed payment → needs a card → automatic top-ups paused → a
 * settlement held for review → automatic limit reached → trial ending →
 * below zero → receptionist → Bailey's free questions.
 * A self-employed stylist's summary is in their terms and never mentions a
 * salon figure: their own balance (or that the salon covers marketing),
 * their own meters, their own Bailey Plus.
 */

const pctWord = (m) => `${Math.round(m.allowance > 0 ? (m.used / m.allowance) * 100 : 0)}%`;

/** The busiest meter this month, as a short phrase ("reminders are 18% used"). */
export function notableMeter(usage) {
    const cands = [];
    const rem = usage.reminders;
    if (rem && rem.allowance != null) cands.push({ m: meter(rem.used, rem.allowance), say: (m) => `reminders are ${pctWord(m)} used` });
    const mf = usage.marketingFree;
    if (mf && mf.allowancePence != null) cands.push({ m: meter(mf.usedPence, mf.allowancePence), say: (m) => `${formatPence(m.used)} of your ${formatPrice(m.allowance)} free marketing is used` });
    const b = usage.bailey;
    if (b && b.allowance != null) cands.push({ m: meter(b.used, b.allowance), say: (m) => `you’ve asked me ${m.used} of ${m.allowance} free questions` });
    if (receptionistState(usage).state === 'on') {
        const r = receptionistMeters(usage);
        if (!r.minutes.unlimited) cands.push({ m: r.minutes, say: (m) => `your receptionist has used ${pctWord(m)} of its minutes` });
    }
    if (!cands.length) return '';
    cands.sort((a, c) => (c.m.allowance ? c.m.used / c.m.allowance : 0) - (a.m.allowance ? a.m.used / a.m.allowance : 0));
    return cands[0].say(cands[0].m);
}

/** When a trial ends, if one is running (the plan's, or the upgrade's). */
export function trialEndsAt(usage) {
    const plan = (usage && usage.plan) || {};
    if (plan.trialEndsAt && plan.trialing !== false) return plan.trialEndsAt;
    const up = plan.baileyUpgrade || {};
    return up.chargeNow === false && up.trialEndsAt ? up.trialEndsAt : null;
}

export function baileySummary(usage, { salonName = '', now = new Date() } = {}) {
    if (!usage || !usage.plan) {
        return { lines: ['I couldn’t load your plan just now.'], tone: 'warn', issue: null, action: null };
    }
    const plan = planSummary(usage);
    const upgraded = isUpgraded(usage);
    const se = isSelfEmployedView(usage);
    const where = salonName ? ` at ${salonName}` : '';
    const rs = receptionistState(usage);
    const extras = [upgraded && BAILEY_PLUS, !se && rs.state === 'on' && 'the AI receptionist'].filter(Boolean).join(' and ');
    const first = se
        ? `You’re working${where} as a self-employed stylist${upgraded ? `, with ${BAILEY_PLUS}` : ''}.`
        : `You’re on ${plan.name}${extras ? ` with ${extras}` : ''}${where}.`;

    const bal = usage.balance || {};
    const settle = bal.settlement || {};
    const pence = bal.pence != null ? Number(bal.pence) : null;

    if (settle.failedAt) {
        const due = settle.duePence != null ? Number(settle.duePence) : pence != null && pence < 0 ? -pence : null;
        return {
            lines: [first, `I couldn’t take ${due != null ? formatPence(due) : 'the last payment'} for your messages. Reminders are still sending, but please update your card.`],
            tone: 'bad', issue: 'settlement-failed', action: 'card',
        };
    }
    if (cardState(usage).needsCard) {
        return { lines: [first, 'Add a card so your reminders keep going smoothly.'], tone: 'warn', issue: 'needs-card', action: 'add-card' };
    }
    const spend = autoSpendState(usage);
    if (spend && spend.paused) {
        return { lines: [first, 'I paused automatic top-ups because spending jumped unusually fast. Nothing else has stopped: reminders still send.'], tone: 'warn', issue: 'auto-paused', action: 'resume' };
    }
    if (spend && spend.held) {
        return { lines: [first, spendCopy.held(spend)], tone: 'ok', issue: 'settlement-held', action: null };
    }
    if (spend && spend.reached && spend.capPence != null) {
        return { lines: [first, spendCopy.reached(spend)], tone: 'warn', issue: 'cap-reached', action: 'top-up' };
    }
    const trial = trialEndsAt(usage);
    const trialDate = shortDate(trial, now);
    if (trialDate && new Date(trial) > now && (new Date(trial) - now) / 864e5 <= 14) {
        const price = upgradePricePence(usage);
        const after = usage.plan.trialEndsAt ? '' : price != null ? ` Then ${BAILEY_PLUS} is ${formatPrice(price)} a month.` : '';
        return { lines: [first, `Your free trial ends ${trialDate}.${after}`], tone: 'ok', issue: 'trial-ending', action: null };
    }
    if (pence != null && pence < 0) {
        const due = settle.duePence != null ? Number(settle.duePence) : -pence;
        const when = shortDate(settle.nextAt, now);
        return {
            lines: [first, `Your balance is ${formatPence(pence)}, and that’s fine: reminders are still sending. I’ll settle ${formatPence(due)} on your card${when ? ` on ${when}` : ' at the end of the month'}.`],
            tone: 'warn', issue: 'negative-balance', action: 'top-up',
        };
    }
    if (!se && rs.state === 'on' && rs.overage.capReached) {
        return { lines: [first, 'Your receptionist has reached your monthly limit, so it’s taking messages until the 1st. You can raise the limit below.'], tone: 'warn', issue: 'receptionist-cap', action: 'receptionist' };
    }
    if (!se && rs.state === 'trial' && rs.trial.endsAt) {
        const end = shortDate(rs.trial.endsAt, now);
        const days = (new Date(rs.trial.endsAt) - now) / 864e5;
        if (days <= 3) return { lines: [first, `Your receptionist’s free trial ends ${end}. Add it to keep it answering.`], tone: 'ok', issue: 'receptionist-trial', action: 'receptionist' };
    }
    if (!se && rs.state === 'on' && (Number((usage.receptionist || {}).alertLevel) || 0) >= 100) {
        const extra = rs.overage.chargedPence ? ` ${formatPence(rs.overage.chargedPence)} so far from your balance, up to your ${formatPrice(rs.overage.capPence || 0)} limit.` : '';
        return { lines: [first, `Your receptionist has used this month’s allowance and is still answering.${extra}`], tone: 'ok', issue: 'receptionist-100', action: null };
    }
    if (upgraded && usage.bailey && baileyQuota(usage.bailey).exhausted) {
        const back = shortDate(usage.bailey.resetsAt, now);
        return { lines: [first, `You’ve asked me this month’s ${BAILEY_PLUS} questions${back ? `; they’re back on ${back}` : ''}.`], tone: 'ok', issue: 'bailey-out', action: null };
    } else if (usage.bailey && baileyQuota(usage.bailey).exhausted) {
        const back = shortDate(usage.bailey.resetsAt, now);
        return { lines: [first, `You’ve used this month’s free questions with me${back ? `; they’re back on ${back}` : ''}. ${BAILEY_PLUS} gives you up to 1,000 a month.`], tone: 'ok', issue: 'bailey-out', action: null };
    }

    const parts = [];
    if (se && usage.balanceCoveredBySalon) {
        const busiest = notableMeter(usage);
        const who = salonName || 'The salon';
        return { lines: [first, `${who} covers your marketing, so there’s nothing to top up${busiest ? `, and ${busiest}` : ''} — nothing to worry about this month.`], tone: 'ok', issue: null, action: null };
    }
    if (pence != null) parts.push(se ? `your own balance is ${formatPence(pence)}` : `${formatPence(pence)} in your balance`);
    const busiest = notableMeter(usage);
    if (busiest) parts.push(busiest);
    const calm = parts.length
        ? `${parts.join(' and ').replace(/^./, (c) => c.toUpperCase())} — nothing to worry about this month.`
        : 'Nothing to worry about this month.';
    return { lines: [first, calm], tone: 'ok', issue: null, action: null };
}

/**
 * This location's monthly total, in pence, from what the plan says:
 * the plan, self-employed seats, the add-ons if on, and numbers beyond
 * those included. null without a base price.
 */
export function monthlyTotal(usage) {
    const plan = (usage && usage.plan) || {};
    if (plan.monthlyTotalPence != null) return Number(plan.monthlyTotalPence);
    if (isSelfEmployedView(usage)) return null; // never a salon figure
    if (plan.basePricePence == null) return null;
    let total = Number(plan.basePricePence) || 0;
    if (plan.type !== 'solo') total += (Number(plan.seSeats) || 0) * (Number(plan.sePricePence) || 0);
    const bundle = plan.bundle && plan.bundle.active && plan.bundle.pricePence != null ? Number(plan.bundle.pricePence) : null;
    if (bundle != null) total += bundle;
    else {
        if (isUpgraded(usage)) total += Number(upgradePricePence(usage)) || 0;
        if (receptionistState(usage).active) total += Number(receptionistState(usage).pricePence) || 0;
    }
    const n = usage.numbers || {};
    const extra = Math.max(0, (Number(n.active) || 0) - (Number(n.included) || 0));
    if (n.pricePence != null) total += extra * Number(n.pricePence);
    return total;
}

/** The next bill date, if the API sends one (plan.nextBillAt / renewsAt / currentPeriodEnd). */
export function nextBillAt(usage) {
    const plan = (usage && usage.plan) || {};
    return plan.nextBillAt || plan.renewsAt || plan.currentPeriodEnd || null;
}
